import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  onToggleDarkMode: () => void;
  isDarkMode: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  currentPage,
  onPageChange,
  onToggleDarkMode,
  isDarkMode
}) => {
  const { vellrixUser, logout } = useAuth();
  const [showKeyboardHints, setShowKeyboardHints] = useState(false);

  const navItems = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard' },
    { id: 'tasks', icon: '📋', label: 'Task Board' },
    { id: 'upload', icon: '📤', label: 'Upload' },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
  ];

  const handleShowShortcuts = () => {
    setShowKeyboardHints(true);
    setTimeout(() => setShowKeyboardHints(false), 3000);
  };

  return (
    <aside className="w-64 flex flex-col" style={{ background: 'var(--bg-sidebar)', color: 'var(--text-sidebar)' }}>
      {/* Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="text-xl font-bold">OpActs<sup className="text-xs">™</sup></div>
        <div className="text-xs opacity-75">Vellrix Systems, Inc</div>
      </div>

      {/* Navigation */}
      <nav className="p-2 space-y-1 flex-1">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onPageChange(item.id)}
            className={`w-full text-left px-3 py-2 rounded hover:bg-gray-800 transition-colors ${
              currentPage === item.id ? 'bg-gray-800' : ''
            }`}
          >
            {item.icon} {item.label}
          </button>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 space-y-2 border-t border-gray-800">
        <div className="text-xs opacity-75">
          Source: <span>ERP Upload</span>
        </div>
        <div className="text-xs opacity-75">
          User: {vellrixUser?.full_name}
        </div>
        <div className="text-xs opacity-75">
          Company: {vellrixUser?.company_id}
        </div>
        <button
          onClick={onToggleDarkMode}
          className="w-full text-left px-2 py-1 rounded hover:bg-gray-800 text-xs transition-colors"
        >
          🌓 Toggle {isDarkMode ? 'Light' : 'Dark'} Mode
        </button>
        <button
          onClick={handleShowShortcuts}
          className="w-full text-left px-2 py-1 rounded hover:bg-gray-800 text-xs transition-colors"
        >
          ⌨️ Keyboard Shortcuts
        </button>
        <button
          onClick={logout}
          className="w-full text-left px-2 py-1 rounded hover:bg-gray-800 text-xs text-red-400 transition-colors"
        >
          🚪 Sign Out
        </button>
      </div>

      {/* Keyboard Hints Overlay */}
      {showKeyboardHints && (
        <div
          className="fixed bottom-20 right-20 p-4 rounded-lg border z-50 transition-opacity"
          style={{
            background: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)'
          }}
        >
          <div className="font-bold mb-2">Keyboard Shortcuts</div>
          <div className="text-sm space-y-1">
            <div>Ctrl+S: Archive Shift</div>
            <div>Ctrl+E: Export PDF</div>
            <div>Ctrl+Z: Undo</div>
            <div>Ctrl+Y: Redo</div>
            <div>Ctrl+D: Toggle Dark Mode</div>
            <div>Ctrl+F: Focus Filters</div>
            <div>Ctrl+R: Reset Filters</div>
            <div>Ctrl+1-4: Switch Pages</div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;